import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ListPage, type ListPageColumn } from '@/components/templates/list-page'
import { Badge } from '@/components/ui/badge'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, getJSON, patchJSON } from '@/lib/api'
import { useViewOnly } from '@/components/templates/app-shell'

interface OpenInvoice {
  id: number
  number: number
  balance: string
  balance_cents: number
}

interface UnreachableMember {
  id: number
  name: string
  email: string | null
  reason: 'hard_bounce' | 'spam_complaint' | 'missing_email_address'
  reason_label: string
  open_invoices: OpenInvoice[]
}

interface UnreachableMembersResponse {
  unreachable_members: UnreachableMember[]
}

const columns: ListPageColumn<UnreachableMember>[] = [
  {
    id: 'member',
    header: 'Member',
    cell: (item) => <span className="font-medium text-foreground">{item.name}</span>,
  },
  { id: 'email', header: 'Email', cell: (item) => item.email ?? 'No address' },
  {
    id: 'reason',
    header: 'Reason',
    cell: (item) => <Badge variant="destructive">{item.reason_label}</Badge>,
  },
  {
    id: 'invoices',
    header: 'Open Invoices',
    cell: (item) =>
      item.open_invoices.length > 0
        ? item.open_invoices.map((invoice) => `#${invoice.number} · ${invoice.balance}`).join(', ')
        : '—',
  },
]

export function UnreachableMembersPage({ onOpenMember }: { onOpenMember: (id: number) => void }) {
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<UnreachableMember | null>(null)
  const [reason, setReason] = useState('')
  const query = useQuery({
    queryKey: ['unreachable-members'],
    queryFn: () => getJSON<UnreachableMembersResponse>('/api/v1/unreachable_members'),
    retry: false,
  })
  const unsuppress = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      patchJSON(`/api/v1/unreachable_members/${id}/unsuppress`, { reason }),
    onSuccess: () => {
      setSelected(null)
      setReason('')
      queryClient.invalidateQueries({ queryKey: ['unreachable-members'] })
    },
  })

  return (
    <>
      <ListPage
        title="Can't reach"
        table={{
          rows: query.data?.unreachable_members ?? [],
          columns,
          getRowId: (item) => item.id,
          getRowLabel: (item) => `${item.name}, ${item.reason_label}`,
          onRowOpen: (item) => onOpenMember(item.id),
          actions: (item) =>
            viewOnly
              ? []
              : item.reason === 'missing_email_address'
                ? []
                : [
                    {
                      label: 'Unsuppress email',
                      onSelect: () => {
                        unsuppress.reset()
                        setSelected(item)
                      },
                    },
                  ],
          state: query.isLoading ? 'loading' : query.isError ? 'error' : 'ready',
          loadingTitle: "Loading members the Association can't reach",
          emptyTitle: 'Every member is reachable',
          errorTitle: "Can't-reach list unavailable",
          errorDescription: 'Refresh and try again.',
        }}
      />
      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsuppress {selected?.email}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="unsuppress-reason">Reason</Label>
            <Input
              id="unsuppress-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          {unsuppress.error && <Alert>{unsuppressErrorMessage(unsuppress.error)}</Alert>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => selected && unsuppress.mutate({ id: selected.id, reason })}
              disabled={reason.trim().length === 0 || unsuppress.isPending}
            >
              Unsuppress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function unsuppressErrorMessage(error: Error) {
  if (error instanceof ApiError && hasErrors(error.body)) return error.body.errors.join(', ')

  return 'Email could not be unsuppressed. Try again.'
}

function hasErrors(body: unknown): body is { errors: string[] } {
  if (typeof body !== 'object' || body === null || !('errors' in body)) return false

  return Array.isArray(body.errors) && body.errors.every((error) => typeof error === 'string')
}
